import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.aajkyabanega.com/api';

// Create a dedicated API instance for meals
const api = axios.create({
  baseURL: API_URL
});

// Add authentication token to all requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  return Promise.reject(error);
});

export const mealsAPI = {
  // Get meal suggestions based on filters and history
  getSuggestions: (filters) => api.post('/meals/suggestions', filters),

  // Get user's meal history
  getMealHistory: () => api.get('/meals/history'),

  // Log a meal
  logMeal: (mealData) => api.post('/meals/log', mealData),

  // Get user's favorite meals
  getFavorites: () => api.get('/meals/favorites'),

  // Add meal to favorites
  addToFavorites: (mealId) => api.post(`/meals/favorites/${mealId}`),

  // Remove meal from favorites
  removeFromFavorites: (mealId) => api.delete(`/meals/favorites/${mealId}`),

  // Update favorite details like folder
  updateFavoriteFolder: (favoriteId, folder) => api.put(`/meals/favorites/${favoriteId}`, { folder }),

  // Share meal plan
  shareMealPlan: (planData) => api.post('/meals/share', planData),
  
  // Meal Planning Endpoints
  getMealPlans: (dateRange) => {
    let url = '/meal-plans';
    if (dateRange?.startDate && dateRange?.endDate) {
      url += `?startDate=${dateRange.startDate.toISOString()}&endDate=${dateRange.endDate.toISOString()}`;
    }
    return api.get(url);
  },
  
  addMealPlan: (mealPlanData) => api.post('/meal-plans', mealPlanData),
  
  updateMealPlan: (id, mealPlanData) => api.put(`/meal-plans/${id}`, mealPlanData),
  
  deleteMealPlan: (id) => api.delete(`/meal-plans/${id}`),
  
  // Add AI meal to favorites (for AI meal suggestions)
  addAiMealToFavorites: async (meal) => {
    console.log('Saving AI meal to favorites:', meal);
    
    // Ensure the meal object has required fields
    const enhancedMeal = {
      ...meal,
      // Ensure these fields exist for the backend model
      id: meal.id || `ai-meal-${Date.now()}`, // Ensure we have an ID
      mealType: meal.mealType || 'dinner',
      dietaryType: meal.dietaryType || ['vegetarian'],
      cuisineStyle: meal.cuisineStyle || ['healthy'],
      tags: meal.tags || [],
      // Add any other required fields with defaults if needed
    };
    
    try {
      // Check auth token to make sure we're authenticated
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        throw new Error('You must be logged in to save favorites');
      }
      
      // Make the API call
      const response = await api.post('/meals/favorites', {
        meal: enhancedMeal,
        folder: 'AI Suggestions',
        notes: `Recommended by AI (${new Date().toLocaleDateString()})`
      });
      
      console.log('Successfully saved to favorites:', response.data);
      return response;
    } catch (error) {
      console.error('Error in addAiMealToFavorites:', error.response || error);
      throw error;
    }
  },
};

// Admin API endpoints
export const adminMealsAPI = {
  // Get all meal ideas
  getAllMeals: async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Admin token available:', !!token);
      return await api.get('/admin/meals');
    } catch (error) {
      console.error('Error fetching meals:', error.response?.data || error.message);
      throw error;
    }
  },

  // Add new meal idea
  addMeal: async (mealData) => {
    try {
      console.log('Sending meal data to server:', mealData);
      
      // Ensure all necessary arrays are properly formatted
      const formattedData = {
        ...mealData,
        mealType: mealData.mealType,
        dietaryType: Array.isArray(mealData.dietaryType) ? mealData.dietaryType : [mealData.dietaryType].filter(Boolean),
        cuisineStyle: Array.isArray(mealData.cuisineStyle) ? mealData.cuisineStyle : [mealData.cuisineStyle].filter(Boolean),
        tags: Array.isArray(mealData.tags) ? mealData.tags : (mealData.tags ? [mealData.tags] : []),
        seasonality: Array.isArray(mealData.seasonality) ? mealData.seasonality : [mealData.seasonality].filter(Boolean)
      };
      
      // Make direct API call with explicit headers
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required. Please log in as an admin.');
      }
      
      const response = await api.post('/admin/meals', formattedData);
      console.log('Meal saved successfully:', response.data);
      return response;
    } catch (error) {
      console.error('Error adding meal:', error.response?.data || error.message);
      // If we're in development and this is likely a connection error, use mock implementation
      if (process.env.NODE_ENV === 'development' && !error.response) {
        console.log('Using mock implementation for addMeal');
        // Return mock successful response
        return { data: { ...mealData, _id: String(Date.now()) } };
      }
      throw error;
    }
  },

  // Update meal idea
  updateMeal: async (mealId, mealData) => {
    try {
      console.log(`Updating meal ${mealId} with:`, mealData);
      return await api.put(`/admin/meals/${mealId}`, mealData);
    } catch (error) {
      console.error('Error updating meal:', error.response?.data || error.message);
      throw error;
    }
  },

  // Delete meal idea
  deleteMeal: async (mealId) => {
    try {
      return await api.delete(`/admin/meals/${mealId}`);
    } catch (error) {
      console.error('Error deleting meal:', error.response?.data || error.message);
      throw error;
    }
  },

  // Manage preferences and filters
  getPreferences: async () => {
    try {
      return await api.get('/admin/preferences');
    } catch (error) {
      console.error('Error fetching preferences:', error.response?.data || error.message);
      throw error;
    }
  },
  updatePreferences: async (data) => {
    try {
      return await api.put('/admin/preferences', data);
    } catch (error) {
      console.error('Error updating preferences:', error.response?.data || error.message);
      throw error;
    }
  },
  addPreference: async (data) => {
    try {
      return await api.post('/admin/preferences', data);
    } catch (error) {
      console.error('Error adding preference:', error.response?.data || error.message);
      throw error;
    }
  },
  deletePreference: async (id) => {
    try {
      return await api.delete(`/admin/preferences/${id}`);
    } catch (error) {
      console.error('Error deleting preference:', error.response?.data || error.message);
      throw error;
    }
  },
};

// send notification to all users
export const sendNotification = async (data) => {
  try {
    return await api.post('/notifications/sendNotificationFromCRM', data);
  } catch (error) {
    console.error('Error sending notification:', error.response?.data || error.message);
    throw error;
    }
  };
  // get all notifications
  export const getNotifications = async ({ page = 1, limit = 10 } = {}) => {
    try {
      return await api.get(`/notifications/getNotificationScheduledByCRM?page=${page}&limit=${limit}`);
    } catch (error) {
      console.error('Error fetching notifications:', error.response?.data || error.message);
      throw error;
    }
  };
  
  // update notification status
export const updateNotificationStatus = async (id, data) => {
    try {
      return await api.put(`/notifications/editNotificationScheduledByCRM/${id}`,  data );
    } catch (error) {
      console.error('Error updating notification status:', error.response?.data || error.message);
      throw error;
    }
  };
  //delete notification
export const deleteNotification = async (id) => {
    try {
      return await api.delete(`/notifications/deleteNotificationScheduledByCRM/${id}`);
    } catch (error) {
      console.error('Error deleting notification:', error.response?.data || error.message);
      throw error;
    }
  }
